/**
 * Form component for entering basic SIM metadata such as name, description, and type.
 *
 * @module components/sim/BasicInfo
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Paper from "@material-ui/core/Paper";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import moment from "moment";
import {
    Grid,
    NativeSelect,
    FormControl,
    Divider,
    Radio,
    InputLabel,
    TextField,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Tooltip,
    TextareaAutosize,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import Alert from '@material-ui/lab/Alert';
import { connect } from "react-redux";
import CloseIcon from "@material-ui/icons/Close";
import StyledLink from "../common/StyledLink";
import {
    addBasicInfo,
    increaseStep,
    fetchProductList,
    fetchSimDetails,
    reset,
    saveCurrentSimProductType
} from "../../redux/sims/actions";
import TagsInput from 'react-tagsinput-special';
import { sfReset } from "../../redux/sf/actions";
import { useBasicInfoStyles, BootstrapInput } from "./BasicInfo.js";
import {
    removePdf,
    addCaptivate,
    uploadSIMTutorial,
    addCaptivateTutorial,
    removeUploads,
    getPresignedUrl, uploadToPresignedUrl, uploadSIMAttachment
} from "../../services/result.service";
import LinearProgress from '@material-ui/core/LinearProgress';

/**
 * BasicInfo is a React functional component responsible for managing and handling
 * the state, events, and operations for a simulation setup UI. It interacts with
 * the API, fetches and updates simulation details, manages validation, user input,
 * and the dynamic rendering of fields.
 *
 * Functionalities:
 * - Fetch simulation details and products from an API.
 * - Manage state for form fields, file attachments, alerts, and loaders.
 * - Handle user input from text fields and select boxes.
 * - Provide form validation and state updating functions.
 * - Manage attachment size limits and related operations.
 *
 * Props:
 * - newSim {Object}: Initial data for setting up a new simulation. Used to set the initial state.
 * - addBasicInfo {Function}: Function for sending updated simulation data. Called when modifying basic info.
 * - increaseStep {Function}: Callback for advancing the setup process or form workflow.
 * - fetchProductList {Function}: Fetches the available products list for selection in the simulation setup.
 * - products {Array}: Array of available product objects.
 * - fetchSimDetails {Function}: Fetches simulation details by given simulation ID.
 * - reset {Function}: Function to reset the state when creating a fresh simulation.
 * - sfReset {Function}: Specialized reset function, performs additional state cleanup.
 * - saveCurrentSimProductType {Function}: Saves the type of current simulation product.
 */
const BasicInfo = ({
   newSim,
   addBasicInfo,
   increaseStep,
   saveCurrentSimProductType
}) => {
    const MAX_ATTACHMENTS_PER_SIM = 5; // Made a constant in case we ever need to increase this amount. If we need to remove the limit altogether, just set the value to -1
    const TUTORIAL_MAX_SIZE = 1024 * (1024*1024); // Tutorial video max file size is 1GB

    const classes = useBasicInfoStyles();
    const companyDetails = JSON.parse(localStorage.getItem('cd'));

    const [selectedFile, setSelectedFile] = React.useState([]);
    const [uploadInProgress, setUploadInProgress] = React.useState(false);
    const [links, setLinks] = React.useState([]);
    const [linearLoader, setLinearLoader] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertSeverity, setSeverity] = useState('');
    const [alertMesage, setAlertMessage] = useState('');
    const [linearTutorialLoader, setLinearTutorialLoader] = useState(false);
    const [linearAttachmentLoader, setLinearAttachmentLoader] = useState(false);
    const [linearCaptivateTutorial, setLinearCaptivateTutorialLoader] = useState(false);
    const [products, setProducts] = useState([]);
    const [callTypes, setCallTypes] = useState([]);
    const [productOptions, setProductOptions] = useState([])
    const [currentProduct, setCurrentProduct] = useState(null);
    const [data, setData] = useState({
        ...newSim,
        createdOn: moment().format("MMM DD YYYY"),
    });

    const [state, setState] = useState({
        title: newSim.title,
        description: newSim.description,
        brief_description: newSim.brief_description,
        product_id: newSim.product_id,
        sim_type: newSim.sim_type,
        auto_populate: newSim.auto_populate,
        real_time_feedback: newSim.real_time_feedback,
        admin_note: newSim.admin_note,
        draft: newSim.draft,
        call_type: newSim.call_type,
        natural_lenguage: newSim.natural_lenguage,
    });

    useEffect(
        () => {
            setState((prev) => ({
                ...prev,
                title: newSim.title,
                description: newSim.description,
                brief_description: newSim.brief_description,
                product_id: newSim.product_id,
                sim_type: newSim.sim_type,
                auto_populate: newSim.auto_populate,
                real_time_feedback: newSim.real_time_feedback,
                admin_note: newSim.admin_note,
                draft: newSim.draft,
                call_type: newSim.call_type,
                natural_lenguage: newSim.natural_lenguage,
            }));

            if (newSim?.documents?.length) {
                setSelectedFile(newSim.documents);
            }
            if (newSim?.hot_links?.length) {
                setLinks(newSim.hot_links);
            }
            if (newSim.captivate_folder_name) {
                setState((prev) => ({
                    ...prev,
                    captivate_folder_name: newSim.captivate_folder_name,
                }));
            }
            if (newSim.tutorial_video_name) {
                setState((prev) => ({
                    ...prev,
                    tutorial_video_name: newSim.tutorial_video_name,
                }));
            }
            if (newSim.captivate_learning_video_name) {
                setState((prev) => ({
                    ...prev,
                    captivate_demo_video: newSim.captivate_learning_video_name,
                }));
            }

            setData(newSim);

            if (newSim.id && !localStorage.getItem("sims_id")) {
                localStorage.setItem('sims_id', newSim.id);
            }

            setProducts(companyDetails.products);
            setCallTypes(companyDetails.callTypes);
        },
        [newSim/*, products*/]
    );

    useEffect(
        () => {
            if (openAlert) {
                setTimeout(() => {
                    setOpenAlert(false);
                }, 3000);
            }
        },
        [openAlert]
    );

    useEffect(
        () => {
            let options = products.map((product) => {
                return {label: product.name, value: product._id};
            });
            options = Object.values(options).sort(
                (prod1, prod2) => {
                    return prod1.label.localeCompare(prod2.label);
                }
            )
            setProductOptions(options);
        },
        [products]
    );

    useEffect(
        () => {
            productOptions.map((product) => {
                if (
                    (state.sim_type === 'hiring') &&
                    (product.label.indexOf('Hiring') > -1)
                ) {
                    setCurrentProduct(product.value);
                } else {
                    if (!currentProduct && (product.label.indexOf('Voice Only'))) {
                        setCurrentProduct(product.value);
                    }
                }
            });
        },
        [productOptions]
    );

    /**
     * Updates the existing state data with new character data.
     *
     * @function
     * @param {Object} characterData - An object containing the updated character-related data.
     *                                 The properties in this object will overwrite or add to
     *                                 the existing properties in the state data.
     * @returns {void}
     */
    const updateData = (characterData) => {
        setData((prevData) => ({...prevData, ...characterData}));
    };

    /**
     * An asynchronous event handler function that updates the component's state
     * based on the input field's name and value.
     *
     * @function handleChange
     * @async
     * @param {Event} evt - The event object triggered by the input element.
     * @property {Object} evt.target - The target element responsible for the event.
     * @property {string} evt.target.name - The name attribute of the input element.
     * @property {string} evt.target.value - The value of the input element.
     *
     * @description
     * Extracts the input field's name and its value from the event object,
     * and updates the component state by maintaining the previous state
     * while dynamically setting the value corresponding to the input's name.
     */
    const handleChange = async (evt) => {
        const {value} = evt.target;
        setState({
            ...state,
            [evt.target.name]: value,
        });
    };

    /**
     * Handles the change event for a select box and updates the application state accordingly.
     *
     * @async
     * @function handleSelectBoxChange
     * @param {Object} evt - The event object triggered by the change event on the select box.
     * @param {Object} evt.target - The target element where the event occurred.
     * @param {string} evt.target.name - The name attribute of the target element.
     * @param {string} evt.target.value - The selected value in the select box.
     *
     * Updates:
     * - Updates the state based on the `name` and `value` of the select box.
     * - If the select box name is `product_id`, modifies the `sim_type` field in the state based on the selected product.
     * - Calls `saveCurrentSimProductType` to save the current product type based on the selection.
     * - Calls `addBasicInfo` to update additional data if `data.id` exists.
     * - Calls `updateData` to propagate the updated state.
     *
     * Dependencies:
     * - Updates the state using `setState`.
     * - Calls `saveCurrentSimProductType` to save the product type.
     * - Uses `addBasicInfo` to send updated state information to an external resource if `data.id` exists.
     * - Calls `updateData` to propagate the updated state to other parts of the application.
     */
    const handleSelectBoxChange = async (evt) => {
        const { value } = evt.target;
        const requestData = {...state};

        if (evt.target.name === 'product_id') {
            setCurrentProduct(value);
            requestData.product_id = value;
            for (const product of products) {
                if (product._id === value) {
                    requestData.sim_type = product.name ==="Hiring SIM" ? "hiring" : "training";
                    saveCurrentSimProductType(product.name);
                }
            }
        } else {
            requestData.call_type = value;
        }

        setState(requestData);
        if (data.id) {
            const formData = new FormData();
            requestData._id = data.id;
            const details = JSON.stringify(requestData);
            formData.append("data", details);
            await addBasicInfo(formData, requestData);
        }
        updateData(requestData);
    };

    /**
     * Asynchronously validates and updates the state based on user interaction events.
     *
     * The `validate` function performs the following:
     * 1. Checks if the `state.title` is defined.
     * 2. Constructs and updates data by calling the `updateData` function.
     * 3. Handles changes specifically for the `title`, `description`, `brief_description`, or `admin_note` fields.
     * 4. Constructs a request with updated state and sends it via the `addBasicInfo` function.
     *
     * @param {Object} evt - The event object triggered by user interaction.
     * @param {string} evt.target.name - The name of the field being updated.
     * @param {string} evt.target.value - The value of the field being updated.
     * @returns {Promise<void>} A promise that resolves once the validation and updates are complete.
     */
    const validate = async (evt) => {
        if (state.title) {
            const res = {selectSimType: {}};
            res.title = state.title;
            res.description = state.description;
            res.brief_description = state.brief_description;
            res.product_id = state.product_id;
            res.sim_type = state.sim_type;
            res.auto_populate = state.auto_populate;
            res.real_time_feedback = state.real_time_feedback;
            res.natural_lenguage = state.natural_lenguage;
            res.draft = state.draft;
            res.admin_note = state.admin_note;
            res.call_type = state.call_type;
            updateData(res);

            if (
                evt.target.name === 'title' ||
                evt.target.name === 'description' ||
                evt.target.name === 'brief_description' ||
                evt.target.name === 'admin_note'
            ) {
                const { value } = evt.target;
                const requestData = {};
                const formData = new FormData();
                if (data.id) {
                    requestData._id = data.id;
                }
                const currentState = {...state, [evt.target.name]: value};
                requestData.title = currentState.title;
                requestData.description = currentState.description;
                requestData.brief_description = currentState.brief_description;
                requestData.admin_note = currentState.admin_note;
                requestData.product_id = currentState.product_id ? currentState.product_id : products[0]._id;
                requestData.call_type = currentState.call_type ? currentState.call_type : callTypes[0];
                const details = JSON.stringify(requestData);
                formData.append("data", details);
                await addBasicInfo(formData, requestData);
            }
        }
    };

    /**
     * An asynchronous event handler function that manages changes to the state when a checkbox is toggled.
     * Updates the component state with the checkbox's new value and optionally sends an update request to the server
     * if a data object with an `id` is present.
     *
     * @param {Object} evt - The event object from the checkbox input element.
     * @param {Object} evt.target - The target DOM element of the event.
     * @param {string} evt.target.name - The name attribute of the checkbox input.
     * @param {boolean} evt.target.checked - The checked status of the checkbox input.
     */
    const handleCheckboxChange = async (evt) => {
        setState({...state, [evt.target.name]: evt.target.checked});
        if (data.id) {
            const requestData = {};
            const formData = new FormData();
            requestData._id = data.id;
            const currentState = {...state, [evt.target.name]: evt.target.checked};
            requestData.auto_populate = currentState.auto_populate;
            requestData.real_time_feedback = currentState.real_time_feedback;
            requestData.natural_lenguage = currentState.natural_lenguage;
            requestData.draft = currentState.draft;
            const details = JSON.stringify(requestData);
            formData.append("data", details);
            await addBasicInfo(formData, requestData);
        }
    };

    /**
     * Handles the next step in a multi-step process, including updating the state with input data,
     * preparing the request payload, sending the data to the server, and advancing the process step.
     *
     * This asynchronous method performs the following operations:
     * - Sets the upload progress indicator to true.
     * - Prepares the data object with required fields.
     * - Converts the data into a FormData object for transmission.
     * - Calls a function to add the basic information to the server.
     * - Updates the upload progress indicator back to false upon completion.
     * - Advances the step in the process sequence.
     *
     * @async
     * @function handleNext
     * @returns {Promise<void>} Resolves when the next step handling is complete.
     */
    const handleNext = async () => {
        setUploadInProgress(true);
        const requestData = {};
        const formData = new FormData();
        if (data.id) {
            requestData._id = data.id;
        }
        requestData.title = data.title;
        requestData.description = data.description;
        requestData.brief_description = data.brief_description;
        requestData.sim_type = data.sim_type;
        requestData.auto_populate = data.auto_populate;
        requestData.real_time_feedback = data.real_time_feedback;
        requestData.natural_lenguage = data.natural_lenguage;
        requestData.draft = data.draft;
        requestData.product_id =
            state.product_id === "" ? products[0]._id : state.product_id;
        requestData.admin_note = state.admin_note;
        // requestData.hot_links =  links;
        const details = JSON.stringify(requestData);
        formData.append("data", details);
        await addBasicInfo(formData, requestData);
        setUploadInProgress(false);
        increaseStep();
    };

    /**
     * Handles the file selection and upload process triggered by a file input event.
     * This function performs the following steps:
     * 1. Extracts the selected file, its name, and MIME type from the event object.
     * 2. Initiates the upload process by requesting a pre-signed URL for the file.
     * 3. Uploads the file to the provided pre-signed URL.
     * 4. Updates relevant data based on the uploaded file's information.
     * 5. Displays appropriate success or error messages to the user.
     *
     * @async
     * @function
     * @param {Object} evt - The event object triggered by the file input change.
     * @param {FileList} evt.target.files - The file list containing the selected file.
     * @throws Will handle and show errors related to fetching pre-signed URLs, uploading files, or updating related data.
     */
    const fileSelected = async (evt) => {
        const file = evt.target.files[0],
            fileName = file.name,
            mimeType = file.type;

        let error = "There was an error uploading the Attachment!";

        setLinearAttachmentLoader(true);
        let signedUrls = await getPresignedUrl('attachment', fileName);
        if (signedUrls && signedUrls.success) {
            let uploaded = await uploadToPresignedUrl(signedUrls.presignedUrl, file);
            if (uploaded && uploaded.success) {
                // TODO - Update the SIM with the information about the uploaded attachment
                let updated = await uploadSIMAttachment(
                    data.id,
                    signedUrls.downloadUrl,
                    fileName,
                    mimeType
                );
                if (updated && updated.success) {
                    const docs = selectedFile.concat(file);
                    setSelectedFile(docs);
                    setData({...data, id: updated.simId});
                    error = "";
                }
            }
        }

        setLinearAttachmentLoader(false);
        if (error) {
            setSeverity("error");
            setOpenAlert(true);
            setAlertMessage(error.toString());
        } else {
            setSeverity("success");
            setOpenAlert(true);
            setAlertMessage("Attachment Uploaded successfully !");
        }
    };

    /**
     * Asynchronously removes a document from the list of selected files.
     *
     * This function creates a copy of the current array of selected files and identifies
     * the index of the specified file in the array, either by `_id` if present, or by `name`.
     * If the specified file is found, it is removed from the array, and the state of the
     * selected files is updated. Additionally, if a unique `id` for the data object is present,
     * the document list within `newSim` is updated. If the file has an `_id`,
     * it triggers the removal of the file in the backend by calling `removePdf`.
     *
     * @async
     * @function
     * @param {Object} file - The file object to be removed. Must have either a `_id` or `name` property.
     * @returns {Promise<void>} Resolves when the file is successfully removed and state is updated.
     */
    const removeDocument = async (file) => {
        const array = [...selectedFile]; // make a separate copy of the array
        const index = file._id
            ? selectedFile.findIndex((sf) => sf._id === file._id)
            : selectedFile.findIndex((sf) => sf.name === file.name);

        if (index !== -1) {
            array.splice(index, 1);
            setSelectedFile(array);
            if (data.id) {
                newSim.documents = array
            }
            if (file._id) {
                await removePdf(data.id, file._id);
            }
        }
    };

    /**
     * Asynchronously removes uploads associated with a specific field.
     *
     * This method clears the value of the given field in the current state
     * and, if an `id` exists in the `data` object, triggers the removal
     * process for uploads associated with the provided field name on the server.
     *
     * @param {string} fieldName - The name of the field for which uploads should be removed.
     * @returns {Promise<void>} Resolves when the state update and potential server-side upload removal are completed.
     */
    const removeUploadsMethod = async (fieldName) => {
        setState({
            ...state,
            [fieldName]: '',
        });
        if (data.id) {
            await removeUploads(data.id, fieldName);
        }
    };

    /**
     * Asynchronous function that handles the change in link data and updates the backend accordingly.
     *
     * @function
     * @param {Object} e - Event or data containing updated link information.
     * @returns {Promise<void>} - A Promise that resolves when the links are updated both locally and in the backend.
     *
     * The function performs the following:
     * - Updates the local state with the new link data using `setLinks`.
     * - If a valid `data.id` exists, prepares a request object containing the updated links and associated ID.
     * - Creates a `FormData` object that encapsulates the serialized request data.
     * - Calls the function `addBasicInfo` to send the updated link data to the backend for persistence.
     */
    const linkChange = async (e) => {
        setLinks(e);
        if (data.id) {
            const requestData = {};
            requestData._id = data.id;
            requestData.hot_links = e;
            const formData = new FormData();
            const details = JSON.stringify(requestData);
            formData.append("data", details);
            await addBasicInfo(formData, requestData);
        }
    };

    /**
     * Handles the selection and upload of a Captivate folder.
     *
     * This asynchronous function processes a file input event to upload a Captivate
     * folder. It appends the selected file to a `FormData` object, updates the component
     * state, and triggers a linear loader to indicate ongoing upload progress. The function
     * calls an external API to handle the upload and updates the application state depending
     * on the success or failure of the operation.
     *
     * @param {Object} evt - The event object triggered by a file input change.
     * @param {Object} evt.target - The target element of the event.
     * @param {Object} evt.target.files - The FileList object containing the selected files.
     * @param {string} evt.target.name - The name attribute of the target input element.
     */
    const selectCaptivateFolder = async(evt) => {
        const formData = new FormData();
        formData.append('folder', evt.target.files[0]);
        setState({
            ...state,
            [evt.target.name]: evt.target.files[0].name,
        });
        setLinearLoader(true);
        await addCaptivate(formData, data.id).then((res) => {
            if (res.success) {
                setSeverity('success');
                setLinearLoader(false);
                setOpenAlert(true);
                setAlertMessage('Captivate Learning Uploaded successfully!');
            } else {
                setSeverity('error');
                setLinearLoader(false);
                setOpenAlert(true);
                setAlertMessage('There is an error in Uploading Captivate Learning!');
            }
        });
    };

    /**
     * Handles the upload of a tutorial file. This asynchronous function is triggered
     * upon a file input event, extracts the file information, validates the file size,
     * and uploads it using presigned URLs.
     *
     * It performs the following steps:
     * - Validates the file size against a predefined limit.
     * - Retrieves presigned URLs for upload and download.
     * - Uploads the file to the provided presigned upload URL.
     * - Sends the download URL and other metadata to update the server or related record.
     * - Updates the relevant state depending on the success or failure of the above steps.
     *
     * In case of an error during the process, an alert is shown with a descriptive error message.
     *
     * @param {object} evt - The event object triggered by the file input.
     * @throws {string} If an error occurs during validation, URL retrieval, file upload, or server update.
     */
    const uploadTutorial = async (evt) => {
        const file = evt.target.files[0],
            fileName = file.name,
            targetName = evt.target.name;

        if (file.size > TUTORIAL_MAX_SIZE) {
            setOpenAlert(true);
            setSeverity('warning');
            setAlertMessage('Video size exceeds more than '+(TUTORIAL_MAX_SIZE/1024)+'MB');
            return;
        }

        setLinearTutorialLoader(true);
        let error = "There was an error Uploading the Video Tutorial!";

        try {
            let url = await getPresignedUrl('tutorial', fileName);
            if (url && url.success) {
                // Upload to presigned upload url
                let result = await uploadToPresignedUrl(
                    url.presignedUrl,
                    file
                );

                if (result && result.success) {
                    let result = await uploadSIMTutorial(
                        data.id,
                        url.downloadUrl,
                        fileName
                    );
                    if (result && result.success) {
                        setState({...state, [targetName]: fileName});
                        error = "";
                    }
                }
            }
        } catch (e) {
            error = e.toString();
        }

        if (error) {
            setSeverity("error");
            setLinearTutorialLoader(false);
            setOpenAlert(true);
            setAlertMessage(error.toString());
        } else {
            setSeverity("success");
            setLinearTutorialLoader(false);
            setOpenAlert(true);
            setAlertMessage("Tutorial Uploaded successfully!");
        }
    };

    /**
     * Handles the upload process for a video in the Captivate demo tutorial.
     *
     * This asynchronous function processes the uploaded video file, checks its size, and uploads it to the server if it meets the size requirements. The function also manages the user interface by setting alerts, severity messages, and loader states during and after the upload process.
     *
     * - If the video file size exceeds 100 MB, a warning alert with a specific message is shown.
     * - If the video file size is within the limit, the file is appended to a FormData object, and an API request is sent to upload the file.
     * - The function updates the application state with the name of the uploaded video file.
     * - Displays appropriate success or error messages based on the server response.
     *
     * @param {Object} evt - The event object from the file input change event.
     * @param {Object} evt.target - The target element of the event.
     * @param {FileList} evt.target.files - The list of files selected in the input element.
     * @param {string} evt.target.name - The name attribute of the input element.
     * @throws {Error} Throws an error if there is an issue in uploading the tutorial.
     */
    const captivateDemoVideo = async (evt) => {
        if (evt.target.files[0].size / 1000000 > 100) {
            setOpenAlert(true);
            setSeverity('warning');
            setAlertMessage('Video size exceeds more than 100mb');
        } else {
            const formData = new FormData();
            formData.append("video_blob", evt.target.files[0]);
            setState({
                ...state,
                [evt.target.name]: evt.target.files[0].name,
            });
            setLinearCaptivateTutorialLoader(true);
            await addCaptivateTutorial(formData, data.id).then((res) => {
                if (res.success) {
                    setSeverity("success");
                    setLinearCaptivateTutorialLoader(false);
                    setOpenAlert(true);
                    setAlertMessage("Captivate Tutorial Uploaded successfully !");
                } else {
                    setSeverity("error");
                    setLinearCaptivateTutorialLoader(false);
                    setOpenAlert(true);
                    setAlertMessage("There is an error in Uploading Captivate Tutorial!");
                }
            });
        }
    };

    /**
     * A function that generates a sorted product selector component.
     *
     * The `productsSelector` function creates a dropdown menu (NativeSelect) of product options,
     * sorted alphabetically by the product's name. The component uses a custom style and
     * Bootstrap-styled input, with an arrow-down icon as the dropdown indicator. It also
     * listens for changes to update the selected product in the application state.
     *
     * @function
     *
     * @returns {JSX.Element} A NativeSelect component listing all products sorted by name with relevant attributes and event handlers applied.
     */
    const productsSelector = () => {
        console.log("CurrentProduct = ", currentProduct);

        return (
            <NativeSelect
                className={classes.select}
                name="product_id"
                value={currentProduct ?? ''}
                onChange={(evt) => handleSelectBoxChange(evt)}
                variant="outlined"
                required
                input={<BootstrapInput />}
                IconComponent={KeyboardArrowDownIcon}
            >
                {productOptions.map((product) => (
                    <option key={product.value} value={product.value}>
                        {product.label}
                    </option>
                ))}

            </NativeSelect>
        );
    };

    /**
     * Generates and returns a custom dropdown select component for selecting call types.
     *
     * - This function creates a sorted list from the `callTypes` object entries, sorting them in ascending
     *   order based on the call type names.
     * - A `NativeSelect` component is rendered, providing a dropdown for available call types.
     * - The select dropdown triggers a `handleSelectBoxChange` function when a selection is made.
     *
     * @function
     * @returns {JSX.Element} Returns a customized dropdown menu for selecting the call type, utilizing the `NativeSelect` component.
     */
    const callTypeSelector = () => {
        let callTypeList = Object.values(callTypes).sort(
            (ct1, ct2) => {
                return ct1.name.localeCompare(ct2.name);
            }
        );
        return (
            <NativeSelect
                className={classes.select}
                name="call_type"
                value={state.call_type}
                onChange={(evt) => handleSelectBoxChange(evt)}
                variant="outlined"
                required
                input={<BootstrapInput />}
                IconComponent={KeyboardArrowDownIcon}
            >
                {callTypeList.map((callType) => (
                    <option value={callType.name} key={callType._id}>
                        {callType.name}
                    </option>
                ))}
            </NativeSelect>
        );
    };

    return (
        <Paper elevation={0}>
            <Toolbar className={classes.root}>
                <Typography
                    className={classes.title}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    Basic Information
                </Typography>
                {openAlert ? (
                    <Alert
                        variant="filled"
                        severity={alertSeverity}
                        className={alertSeverity === 'error' ? classes.errorAlert : ''}
                    >
                        {alertMesage}
                    </Alert>
                ) : null}
                <StyledLink to="/sims">
                    <Button
                        variant="contained"
                        className={classes.cancel}
                        disableElevation
                    >
                        CANCEL
                    </Button>
                </StyledLink>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!(state.title && state.description)}
                    className={classes.nextButton}
                    disableElevation
                    onClick={handleNext}
                >
                    {uploadInProgress && selectedFile.length ? (
                        <CircularProgress color={"secondary"} size={25} />
                    ) : (
                        "NEXT"
                    )}
                </Button>
            </Toolbar>
            <Divider/>

            <form className={classes.root}>
                <div>
                    <Grid className={classes.formHeight} container>
                        <Grid className={classes.colBorderRight} item xs={6}>
                            <div className={classes.paper}>
                                <InputLabel htmlFor="title"
                                            className={classes.inputLabel}
                                >
                                    Title
                                </InputLabel>
                                <TextField
                                    required
                                    variant="outlined"
                                    name="title"
                                    size="small"
                                    error={!state.title}
                                    fullWidth
                                    onBlur={validate}
                                    value={state.title}
                                    className={classes.margin}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className={classes.paper}>
                                <InputLabel htmlFor="title"
                                            className={classes.inputLabel}
                                >
                                    Description
                                </InputLabel>
                                <TextareaAutosize
                                    required
                                    minRows={8}
                                    name="description"
                                    value={state.description}
                                    onBlur={validate}
                                    className={state.description ?
                                        classes.description :
                                        classes.error
                                    }
                                    onChange={handleChange}
                                    data-enable-grammarly="false"
                                />
                            </div>

                            <div className={classes.paper}>
                                <InputLabel htmlFor="title"
                                            className={classes.inputLabel}
                                >
                                    Brief Description
                                </InputLabel>
                                <TextareaAutosize
                                    minRows={16}
                                    name="brief_description"
                                    value={state.brief_description}
                                    onBlur={validate}
                                    className={classes.description}
                                    onChange={handleChange}
                                    data-enable-grammarly="false"
                                />
                            </div>
                            <div className={classes.paper}>
                                <InputLabel htmlFor="title"
                                            className={classes.inputLabel}
                                >
                                    Admin Notes
                                </InputLabel>
                                <TextareaAutosize
                                    required
                                    minRows={8}
                                    name="admin_note"
                                    value={state.admin_note}
                                    onBlur={validate}
                                    className={classes.description}
                                    onChange={handleChange}
                                    data-enable-grammarly="false"
                                />
                            </div>
                        </Grid>
                        <Grid className={classes.colBorderLeft} item xs={6}>
                            <div className={classes.paper}>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <InputLabel htmlFor="product"
                                                    className={classes.inputLabel}
                                        >
                                            Product
                                        </InputLabel>

                                        {productsSelector()}
                                    </Grid>
                                    <Grid item xs={6}>
                                        <InputLabel htmlFor="product" className={classes.inputLabel}>
                                            Call Types
                                        </InputLabel>

                                        {callTypeSelector()}
                                    </Grid>
                                </Grid>
                            </div>
                            <div className={classes.paper}>
                                <InputLabel htmlFor="sim-type" className={classes.inputLabel}>
                                    Select Sim Type
                                </InputLabel>
                                <Radio
                                    className={classes.radio}
                                    checked={state.sim_type === "hiring"}
                                    name="sim_type"
                                    value="hiring"
                                    onBlur={validate}
                                    color="primary"
                                    inputProps={{"aria-label": "Hiring"}}
                                />
                                Hiring (One Level sim)
                                <Radio
                                    checked={state.sim_type !== "hiring"}
                                    value="training"
                                    color="primary"
                                    onBlur={validate}
                                    name="sim_type"
                                    inputProps={{"aria-label": "Training"}}
                                />
                                Training (Multi Level sim)
                            </div>
                            <div className={classes.paper}>
                                <div className={classes.border}>
                                    <FormControl
                                        component="fieldset"
                                        className={classes.formControl}
                                    >
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        value={state.auto_populate}
                                                        checked={state.auto_populate}
                                                        onBlur={validate}
                                                        name="auto_populate"
                                                        onChange={handleCheckboxChange}
                                                        style={{color: "#CED4DA"}}
                                                    />
                                                }
                                                label="Enable auto-populate"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        value="real-time-feedback"
                                                        name="real_time_feedback"
                                                        checked={state.real_time_feedback}
                                                        onBlur={validate}
                                                        onChange={handleCheckboxChange}
                                                        style={{color: "#CED4DA"}}
                                                    />
                                                }
                                                label="Enable Real-time feedback"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        value="natural-lenguage"
                                                        name="natural_lenguage"
                                                        checked={state.natural_lenguage}
                                                        onBlur={validate}
                                                        onChange={handleCheckboxChange}
                                                        style={{color: "#CED4DA"}}
                                                    />
                                                }
                                                label="Enable Natural Language AI"
                                            />
                                        </FormGroup>
                                    </FormControl>
                                </div>
                            </div>
                            <div className={classes.paper}>
                                <div className={classes.border}>
                                    <FormControl
                                        component="fieldset"
                                        className={classes.formControl}
                                    >
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        value="draft"
                                                        name="draft"
                                                        checked={state.draft}
                                                        onBlur={validate}
                                                        onChange={handleCheckboxChange}
                                                        style={{color: "#CED4DA"}}
                                                    />
                                                }
                                                label="Draft"
                                            />
                                        </FormGroup>
                                    </FormControl>
                                </div>
                            </div>
                            <div className={classes.paper}>
                                <Grid container>
                                    <Grid item xs={6} className={classes.colBorderRight}>
                                        <InputLabel className={classes.inputLabel}>Add attachment</InputLabel>
                                        {linearAttachmentLoader ?
                                            <LinearProgress color="primary"
                                                            className={classes.linear}
                                                            style={{'margin-top': '0.5em', 'margin-bottom': '-1em' }}
                                            /> :
                                            null
                                        }
                                        <Grid container>
                                            <Grid item xs={4}>
                                                <Tooltip
                                                    placement="top"
                                                    title={
                                                        selectedFile.length === MAX_ATTACHMENTS_PER_SIM
                                                            ? `Only ${MAX_ATTACHMENTS_PER_SIM} documents can be attached at a time.`
                                                            : ""
                                                    }
                                                >
                                                    <div>
                                                        <Button
                                                            color="primary"
                                                            className={
                                                                selectedFile.length === MAX_ATTACHMENTS_PER_SIM
                                                                    ? classes.browseDisableBtn
                                                                    : classes.browseBtn
                                                            }
                                                            component="label"
                                                            variant="outlined"
                                                        >
                                                            Browse{" "}
                                                            <input
                                                                hidden
                                                                type="file"
                                                                onChange={fileSelected}
                                                            />
                                                        </Button>
                                                    </div>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs={8} className={classes.fileName}>
                                                {selectedFile &&
                                                    selectedFile.map((file, index) => (
                                                        <div key={index}>
                                                            <Tooltip
                                                                title={file && file.name.length > 25 ? file.name : ''}>
                                                                <span className={classes.docs}>
                                                                  { file.name.length > 25
                                                                      ? `${file.name.slice(0, 25)} ...` : file.name}
                                                                    <CloseIcon
                                                                        onClick={() => removeDocument(file)}
                                                                        className={classes.closeIcon}
                                                                        fontSize="small"
                                                                    />
                                                                </span>
                                                            </Tooltip>
                                                            <br/>
                                                        </div>
                                                    ))}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={6} className={classes.colBorderLeft}>
                                        <InputLabel htmlFor="title" className={classes.inputLabel} style={{marginBottom: 10}}>
                                            Add Tutorial Video
                                        </InputLabel>
                                        {linearTutorialLoader ?
                                            <LinearProgress color="primary" className={classes.linear}/> : null}
                                        <Button
                                            color="primary"
                                            component="label"
                                            variant="outlined"
                                            className={classes.linear}
                                        >
                                            Browse
                                            {' '}
                                            <input
                                                hidden
                                                type="file"
                                                accept="video/mp4,video/x-m4v,video/*"
                                                onChange={uploadTutorial}
                                                name="tutorial_video_name"
                                            />
                                        </Button>
                                        <Tooltip title={state && state.tutorial_video_name && state.tutorial_video_name.length > 25 ? state.tutorial_video_name : ''}>
                                          <span className={classes.captivatefilename}>
                                            {state && state.tutorial_video_name && state.tutorial_video_name > 25
                                                ? `${state.tutorial_video_name.slice(0, 25)} ...` : state.tutorial_video_name}
                                              {
                                                  state && state.tutorial_video_name && !linearTutorialLoader &&
                                                  <CloseIcon
                                                      onClick={() => removeUploadsMethod('tutorial_video_name')}
                                                      className={classes.closeIcon}
                                                      fontSize="small"
                                                  />
                                              }
                                          </span>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </div>
                            <div className={classes.paper}>
                                <Grid container>
                                    <Grid item xs={6} className={classes.colBorderRight}>
                                        <InputLabel htmlFor="title" className={classes.inputLabel} style={{marginBottom: 10}}>
                                            Select Captivate Folder
                                        </InputLabel>
                                        {linearLoader ?
                                            <LinearProgress color="primary" className={classes.linear}/> : null}
                                        <Button
                                            color="primary"
                                            component="label"
                                            variant="outlined"
                                            className={classes.linear}
                                        >
                                            Browse
                                            {' '}
                                            <input
                                                hidden
                                                type="file"
                                                accept=".zip,.rar,.7zip"
                                                onChange={selectCaptivateFolder}
                                                name="captivate_folder_name"
                                            />
                                        </Button>
                                        <Tooltip
                                            title={state && state.captivate_folder_name && state.captivate_folder_name.length > 25 ?
                                                state.captivate_folder_name :
                                                ''}
                                        >
                                                <span className={classes.captivatefilename}>
                                                    {state && state.captivate_folder_name && state.captivate_folder_name > 25
                                                        ? `${state.captivate_folder_name.slice(0, 25)} ...` :
                                                        state.captivate_folder_name
                                                    }
                                                    {
                                                        state && state.captivate_folder_name && !linearLoader ?
                                                            <CloseIcon
                                                                onClick={() => removeUploadsMethod('captivate_folder_name')}
                                                                className={classes.closeIcon}
                                                                fontSize="small"
                                                            /> :
                                                            null
                                                    }
                                                </span>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={6} className={classes.colBorderLeft}>
                                        <InputLabel htmlFor="title" className={classes.inputLabel} style={{marginBottom: 10}}>
                                            Select Captivate Learning Demo Video
                                        </InputLabel>
                                        {linearCaptivateTutorial ?
                                            <LinearProgress color="primary"
                                                            className={classes.linear}
                                            /> :
                                            null
                                        }
                                        <Button
                                            color="primary"
                                            component="label"
                                            variant="outlined"
                                            className={classes.linear}
                                        >
                                            Browse
                                            {' '}
                                            <input
                                                hidden
                                                type="file"
                                                accept="video/mp4,video/x-m4v,video/*"
                                                onChange={captivateDemoVideo}
                                                name="captivate_demo_video"
                                            />
                                        </Button>
                                        <Tooltip title={state && state.captivate_demo_video && state.captivate_demo_video.length > 25 ? state.captivate_demo_video : ''}>
                                            <span className={classes.captivatefilename}>
                                                {state && state.captivate_demo_video && state.captivate_demo_video > 25
                                                    ? `${state.captivate_demo_video.slice(0, 25)} ...` : state.captivate_demo_video}
                                                {
                                                    state && state.captivate_demo_video && !linearCaptivateTutorial ?
                                                        <CloseIcon
                                                            onClick={() => removeUploadsMethod('captivate_demo_video')}
                                                            className={classes.closeIcon}
                                                            fontSize="small"
                                                        /> :
                                                        null
                                                }
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </div>

                            <div className={classes.paper}>
                                <InputLabel htmlFor="title" className={classes.inputLabel}>
                                    Add URL link(s)
                                </InputLabel>
                                <TagsInput
                                    onlyUnique
                                    value={links}
                                    className={classes.tagInput}
                                    name="link"
                                    //  onFocusOut={(e) => hotLinkSave(e)}
                                    onChange={linkChange}
                                    inputProps={{
                                        style: { width: '400px' },
                                    }}
                                />
                                <em className={classes.toolBarValue}>
                                    Click on &apos;Enter&apos; button after adding each link.
                                </em>
                            </div>
                        </Grid>
                    </Grid>
                </div>
            </form>
        </Paper>
    );
};

/**
 * Defines the prop types for the BasicInfo component.
 *
 * This component expects the following props:
 * - `name` (string): The name of the individual.
 * - `age` (number): The age of the individual.
 * - `isEmployed` (boolean): Indicates the employment status of the individual.
 *
 * It ensures proper type validation for all the props passed to
 * the BasicInfo component.
 */
BasicInfo.propTypes = {
    newSim: PropTypes.object,
    addBasicInfo: PropTypes.func,
    increaseStep: PropTypes.func,
    fetchProductList: PropTypes.func,
    products: PropTypes.array,
    fetchSimDetails: PropTypes.func,
    reset: PropTypes.func,
    sfReset: PropTypes.func,
    saveCurrentSimProductType: PropTypes.func
};

/**
 * Maps the state from the Redux store to the props for a React component.
 *
 * @param {Object} state - The Redux store state.
 * @returns {Object} An object that contains specific properties from the store's state:
 *                   - `newSim`: Represents the `newSim` property from the `sim` slice of state.
 *                   - `products`: Represents the `products` property from the `sim` slice of state.
 */
const mapStateToProps = (state) => ({
    newSim: state.sim.newSim,
    products: state.sim.products,
});

/**
 * mapDispatchToProps is an object that maps action creators to dispatchable functions.
 * These functions can be used to dispatch actions to the Redux store, enabling components
 * to trigger updates and state changes.
 *
 * Properties:
 * - addBasicInfo: Dispatches an action to add or update basic information.
 * - increaseStep: Dispatches an action to increment a step in a process or workflow.
 * - fetchProductList: Dispatches an action to fetch a list of products.
 * - fetchSimDetails: Dispatches an action to fetch details of a specific SIM card or related information.
 * - reset: Dispatches an action to reset the application state or a portion of it.
 * - sfReset: Dispatches an action to reset specific Salesforce-related states or configurations.
 * - saveCurrentSimProductType: Dispatches an action to save the current SIM product type to the state.
 */
const mapDispatchToProps = {
    addBasicInfo,
    increaseStep,
    fetchProductList,
    fetchSimDetails,
    reset,
    sfReset,
    saveCurrentSimProductType
};

export default connect(mapStateToProps, mapDispatchToProps)(BasicInfo);
